import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSession } from "../lib/session";

type Mode = "signin" | "signup" | "forgot";

export default function Login(): JSX.Element {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);
  const reload = useSession((s) => s.reload);
  const returnTo = params.get("return_to") || params.get("returnUrl") || "/dashboard";

  // If already logged in, bounce to the target.
  useEffect(() => {
    if (!loading && user) nav(returnTo, { replace: true });
  }, [user, loading, returnTo, nav]);

  const qs = `?return_to=${encodeURIComponent(returnTo)}`;
  const [mode, setMode] = useState<Mode>("signin");

  // Shared form state.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      if (mode === "signin") {
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });
        if (!res.ok) throw new Error(((await res.json()) as { error?: string }).error ?? "Sign-in failed.");
        await reload();
        nav(returnTo, { replace: true });
      } else if (mode === "signup") {
        const res = await fetch("/api/auth/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password, display_name: displayName }),
        });
        if (!res.ok) throw new Error(((await res.json()) as { error?: string }).error ?? "Sign-up failed.");
        await reload();
        nav(returnTo, { replace: true });
      } else if (mode === "forgot") {
        const res = await fetch("/api/auth/forgot", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email }),
        });
        if (!res.ok) throw new Error("Could not send reset email.");
        setNotice("If an account exists for that email, we just sent a reset link. Check your inbox.");
      }
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="login-card card stack" style={{ maxWidth: 420, margin: "60px auto" }}>
      <h1>{mode === "signup" ? "Create account" : mode === "forgot" ? "Reset password" : "Sign in"}</h1>

      {mode !== "forgot" ? (
        <>
          <div className="login-oauth">
            <a href={`/auth/twitch/start${qs}`} className="btn btn-primary">Continue with Twitch</a>
            <a href={`/auth/google/start${qs}`} className="btn">Continue with Google</a>
          </div>
          <div className="login-divider"><span>or with email</span></div>
        </>
      ) : null}

      <form onSubmit={submit} className="stack" style={{ gap: 10 }}>
        {mode === "signup" ? (
          <label className="stack" style={{ gap: 4 }}>
            <span className="login-lbl">Display name</span>
            <input
              type="text"
              required
              maxLength={60}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Frankie Streams"
              className="login-input"
              autoComplete="name"
            />
          </label>
        ) : null}

        <label className="stack" style={{ gap: 4 }}>
          <span className="login-lbl">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="login-input"
            autoComplete="email"
            autoCapitalize="none"
          />
        </label>

        {mode !== "forgot" ? (
          <label className="stack" style={{ gap: 4 }}>
            <span className="login-lbl">Password</span>
            <input
              type="password"
              required
              minLength={mode === "signup" ? 8 : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "min 8 chars, 1 letter, 1 number" : ""}
              className="login-input"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
            />
          </label>
        ) : null}

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy
            ? "Working…"
            : mode === "signup"
            ? "Create account"
            : mode === "forgot"
            ? "Send reset link"
            : "Sign in"}
        </button>
        {err ? <p className="login-err">{err}</p> : null}
        {notice ? <p className="login-notice">{notice}</p> : null}
      </form>

      <div className="login-switch">
        {mode === "signin" ? (
          <>
            <button type="button" className="link-btn" onClick={() => { setMode("signup"); setErr(null); setNotice(null); }}>
              New here? Create an account
            </button>
            <button type="button" className="link-btn" onClick={() => { setMode("forgot"); setErr(null); setNotice(null); }}>
              Forgot password
            </button>
          </>
        ) : (
          <button type="button" className="link-btn" onClick={() => { setMode("signin"); setErr(null); setNotice(null); }}>
            ← Back to sign in
          </button>
        )}
      </div>

      <p className="login-fineprint">
        By continuing you agree to our <Link to="/terms">Terms</Link> and{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <style>{`
        .login-card {
          background: var(--bg-card, #16161f);
          border: 1px solid var(--line, #2a2a3d);
          color: var(--text-pri, #EFEFF1);
        }
        .login-card h1 { color: var(--text-pri); margin-top: 0; }
        .login-card p { color: var(--text-sec); }
        .login-card .login-fineprint { font-size: 12px; color: var(--text-mute); }
        .login-oauth { display: flex; flex-direction: column; gap: 8px; }
        .login-divider { position: relative; text-align: center; margin: 14px 0 8px; }
        .login-divider::before, .login-divider::after {
          content: ""; position: absolute; top: 50%; width: 38%; border-top: 1px solid var(--line);
        }
        .login-divider::before { left: 0; }
        .login-divider::after { right: 0; }
        .login-divider span { font-family: var(--f-mono); font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: var(--text-mute); background: var(--bg-card, #16161f); padding: 0 10px; position: relative; }
        .login-lbl { font-family: var(--f-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--text-mute); }
        .login-input {
          padding: 10px 12px; background: var(--bg-deep, #0e0e10);
          border: 1px solid var(--line); color: var(--text-pri); border-radius: 6px;
          font: inherit; font-size: 14px; width: 100%;
        }
        .login-input:focus { outline: 1px solid var(--gold, #FFD700); outline-offset: -1px; }
        .login-err { color: var(--danger, #ff5252); font-size: 13px; margin: 0; }
        .login-notice { color: var(--ok, #4caf50); font-size: 13px; margin: 0; }
        .login-switch { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .link-btn { background: none; border: 0; color: var(--gold, #FFD700); cursor: pointer; font: inherit; font-size: 12px; padding: 0; text-decoration: underline; }
        [data-theme="light"] .login-card {
          background: #FFFFFF;
          border-color: var(--line);
          box-shadow: 0 1px 3px rgba(0,0,0,.05);
        }
        [data-theme="light"] .login-divider span { background: #fff; }
        [data-theme="light"] .login-card .btn:not(.btn-primary) {
          background: #FFFFFF;
          border-color: var(--line-hot);
          color: var(--text-pri);
        }
        [data-theme="light"] .login-card .btn:not(.btn-primary):hover {
          background: var(--bg-elev);
        }
      `}</style>
    </section>
  );
}
